import type { AgentLibraryEntry, AgentPromptTarget } from '@cdf/api-client-react';
import { getStandardNavigationCatalogue } from '@cdf/navigation-catalogue';

export interface PromptLibraryContext {
  functionKey?: string;
  moduleKey?: string;
}
export interface PromptTargetOption {
  target: AgentPromptTarget;
  label: string;
}
export const GENERAL_PROMPT_TARGETS: AgentPromptTarget[] = [{ kind: 'general' }];
const functions = getStandardNavigationCatalogue().filter(item => item.moduleKey);

export function promptTargetOptions(enabledModules: readonly string[], permissions: readonly string[], tenant = false): PromptTargetOption[] {
  return [
    ...enabledModules.map(key => ({ target: { kind: 'module' as const, key }, label: 'Module: ' + key })),
    ...functions.filter(item => enabledModules.includes(item.moduleKey!) && (tenant || (item.permissions ?? []).every(key => permissions.includes(key))))
      .map(item => ({ target: { kind: 'function' as const, key: item.key }, label: 'Function: ' + item.defaultTitle }))
  ];
}
/** Classify only registered canonical route boundaries, never arbitrary URL fragments. */
export function promptContextForRoute(pathname: string): PromptLibraryContext {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const path = normalized.replace(/^\/manage\/tenders(?=\/|$)/, '/manage/proposals');
  if (path === '/agent' || path === '/acquisition/agent' || path === '/acquisition/assistant') return {};
  const item = functions.filter(item => !item.to.includes('?') && (path === item.to || path.startsWith(item.to + '/')))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return item ? { functionKey: item.key, moduleKey: item.moduleKey } : {};
}
export function defaultPromptTargets(context: PromptLibraryContext): AgentPromptTarget[] {
  return context.functionKey ? [{ kind: 'function', key: context.functionKey }]
    : context.moduleKey ? [{ kind: 'module', key: context.moduleKey }] : GENERAL_PROMPT_TARGETS;
}
export function promptTargetLabel(target: AgentPromptTarget): string {
  return target.kind === 'general' ? 'General'
    : target.kind === 'function' ? 'Function: ' + (functions.find(item => item.key === target.key)?.defaultTitle ?? target.key)
      : 'Module: ' + target.key;
}
export function promptOriginLabel(entry: AgentLibraryEntry): string {
  if (entry.visibility === 'personal') return 'Personal · private';
  const origin = entry.origin ?? entry.scope.kind;
  return origin === 'tenant' ? (entry.inherited ? 'Tenant · inherited' : 'Tenant · shared')
    : origin === 'entity' ? 'Entity · shared' : 'Platform · shared';
}
